import { useState, useEffect } from "react";
import { 
  X, DollarSign, RefreshCw, ArrowLeftRight, Receipt, 
  CreditCard, Wallet, ChevronRight, TrendingUp, FileText,
  Building2, Calculator, PiggyBank, Banknote, BarChart2, ArrowLeft, Scale,
  FileStack, AlertCircle, CheckCircle2, Plug
} from "lucide-react";
import RapprochementPage from "./RapprochementPage";
import StatsPage from "./StatsPage";
import { StatistiquesParcoursPage } from "./components/telepro";
import GadsVsRac from "./components/GadsVsRac";
import ConsolidationComptable from "./components/ConsolidationComptable";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function ComptaFinancesDashboard({ onBack, poles, onSelectPole }) {
  const [activeSection, setActiveSection] = useState("rapprochement");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtrer les pôles financiers
  const financialPoles = poles?.filter(p => ['Fin-RAC', 'Fin-Avances', 'Fin-Soldes'].includes(p.name)) || [];

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      // Charger les stats du rapprochement
      const resp = await authFetch(`${API}/rapprochement/stats`);
      if (resp.ok) {
        const data = await resp.json();
        setStats(data);
      }
    } catch (e) {
      console.error("Erreur chargement stats:", e);
    }
    setLoading(false);
  };

  const menuItems = [
    { 
      id: "consolidation", 
      label: "Consolidation Comptable", 
      icon: FileStack,
      description: "Vue unifiée Pennylane + CB",
      color: "violet"
    },
    { 
      id: "rapprochement", 
      label: "Rapprochement Bancaire", 
      icon: ArrowLeftRight,
      description: "Pennylane & Relevés CB",
      color: "emerald"
    },
    { 
      id: "gads-vs-rac", 
      label: "GAds vs RAC", 
      icon: Scale,
      description: "Dépenses pub vs encaissements",
      color: "amber"
    },
    { 
      id: "divider-stats", 
      type: "divider",
      label: "Statistiques"
    },
    { 
      id: "stats-ca", 
      label: "Statistiques & CA", 
      icon: TrendingUp,
      description: "Chiffre d'affaires & analyses",
      color: "blue"
    },
    { 
      id: "stats-parcours", 
      label: "Statistiques Parcours", 
      icon: BarChart2,
      description: "Durées & étapes clients",
      color: "purple"
    },
    { 
      id: "divider1", 
      type: "divider",
      label: "Pôles Financiers"
    },
    ...financialPoles.map(pole => ({
      id: `pole-${pole.id}`,
      label: pole.name,
      icon: pole.name === 'Fin-RAC' ? Receipt : pole.name === 'Fin-Avances' ? Wallet : CreditCard,
      description: `${pole.columns_count} champs`,
      color: "teal",
      pole: pole
    }))
  ];

  const handleMenuClick = (item) => {
    if (item.pole) {
      // Ouvrir le pôle dans la vue principale
      onSelectPole(item.pole);
    } else {
      setActiveSection(item.id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white flex">
      {/* Sidebar */}
      <div className="w-72 bg-slate-950 border-r border-slate-800 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Compta / Finances</h1>
              <p className="text-xs text-slate-400">Gestion comptable</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item, index) => {
            if (item.type === "divider") {
              return (
                <div key={index} className="pt-4 pb-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider px-3">{item.label}</p>
                </div>
              );
            }
            
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const colorClasses = {
              emerald: isActive ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "hover:bg-slate-800 text-slate-400",
              blue: isActive ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "hover:bg-slate-800 text-slate-400",
              purple: isActive ? "bg-purple-500/20 text-purple-400 border-purple-500/30" : "hover:bg-slate-800 text-slate-400",
              teal: isActive ? "bg-teal-500/20 text-teal-400 border-teal-500/30" : "hover:bg-slate-800 text-slate-400",
              amber: isActive ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "hover:bg-slate-800 text-slate-400",
            };
            
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all border border-transparent ${colorClasses[item.color]}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  isActive 
                    ? item.color === 'emerald' ? 'bg-emerald-500/30' 
                    : item.color === 'purple' ? 'bg-purple-500/30'
                    : item.color === 'teal' ? 'bg-teal-500/30'
                    : item.color === 'amber' ? 'bg-amber-500/30'
                    : 'bg-blue-500/30'
                    : 'bg-slate-800'
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 text-left">
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.description && (
                    <p className="text-xs text-slate-500">{item.description}</p>
                  )}
                </div>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </button>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={onBack}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors"
          >
            <X className="w-4 h-4" />
            Retour au CRM
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {activeSection === "rapprochement" && (
          <RapprochementPage onBack={() => {}} embedded={true} />
        )}
        
        {activeSection === "stats-ca" && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <TrendingUp className="w-7 h-7 text-blue-400" />
                Statistiques & CA
              </h2>
              <p className="text-slate-400 mt-1">Analyse du chiffre d'affaires et des performances</p>
            </div>
            <StatsPage embedded={true} />
          </div>
        )}
        
        {activeSection === "stats-parcours" && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <BarChart2 className="w-7 h-7 text-purple-400" />
                Statistiques Parcours
              </h2>
              <p className="text-slate-400 mt-1">Durées moyennes et analyse des étapes clients</p>
            </div>
            <StatistiquesParcoursPage />
          </div>
        )}
        
        {activeSection === "consolidation" && (
          <ConsolidationComptable />
        )}
        
        {activeSection === "gads-vs-rac" && (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <Scale className="w-7 h-7 text-amber-400" />
                GAds vs RAC
              </h2>
              <p className="text-slate-400 mt-1">Comparaison des dépenses Google Ads et des encaissements RAC</p>
            </div>
            <GadsVsRac />
          </div>
        )}
        
        {activeSection === "overview" && (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Vue d'ensemble Finances</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard 
                  icon={ArrowLeftRight} 
                  label="Transactions CB" 
                  value={stats?.cb_transactions || 0} 
                  color="emerald" 
                />
                <StatCard 
                  icon={FileText} 
                  label="Factures Pennylane" 
                  value={stats?.pennylane_invoices || 0} 
                  color="blue" 
                />
                <StatCard 
                  icon={TrendingUp} 
                  label="Rapprochées" 
                  value={stats?.matched || 0} 
                  color="green" 
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400",
    blue: "from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400",
    green: "from-green-500/20 to-green-600/10 border-green-500/30 text-green-400",
  };
  
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-slate-900/50 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-slate-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}
